import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";

import "./OnboardingForm.css";
import { CheckOutlined } from "@ant-design/icons";
import { Button, Form, Steps } from "antd";
import { mapValues } from "lodash";
import { useLocation } from "react-router";
import { toast } from "react-toastify";

import AddCompanySuccessLandingPage from "../../AddCompany/AddCompanySuccessLandingPage";
import NoResults from "../../Common/NoResultsGIF";
import FormLayout from "../FormLayout";
import FormPageLayout from "../FormPageLayout";
import MyForm from "../MyForm";
import OnboardingWelcomePage from "../OnboardingWelcomePage";
import {
  RenderIconOnPreviousClickForCustomerExist, RenderIconOnNextClickForCustomerExist,
} from "./RenderStepsIcon";
import CompanyContactForm from "./SubForms/CompanyContactForm/CompanyContactForm";
import CompanyInfoForm from "./SubForms/CompanyInfoForm";
import FieldsForm from "./SubForms/FieldsForm/FieldsForm";
import LinkReportForm from "./SubForms/LinkReportForm";
import AddLocationChoice from "./SubForms/LocationsForm/AddLocationChoice";
import LocationsForm from "./SubForms/LocationsForm/LocationsForm";
import FormButtons from "./SubForms/ReusableComponents/FormButtons";
import SignupForm from "./SubForms/SignupForm";

import {
  getCustomerConfig, getUserCredentials, isNormalOrPostpayCustomer, normalizeCustomerType,
} from "../../../utils/common";
import { validateEmail, validatePhoneNumber } from "../../Common/utils/validationUtils";
import {
  buildEnvironmentInfoPayload,
  buildInvitationLocations,
  buildOnboardingLocations,
  buildOnboardingUsers,
  buildProductInfoPayload,
  getCompanyIndependentFlag,
  parseCustomerInfoLocationAddress,
} from "../helpers";

import { getCustomerInvitation, getCustomerInfo, submitOnboardingForm } from "../../../actions/onboarding";
import { getCompanyList } from "../../../actions/switchCompany";

import { ReactComponent as AccountCreation } from "../../../assets/icons/account-creation-small.svg";
import { ReactComponent as GenistaLogo } from "../../../assets/icons/genista-logo-small.svg";
import pumpkinIcon from "../../../assets/images/environment/pumpkin.gif";
import { ReactComponent as CustomerInfoCoolDark } from "../../../assets/images/logo/CustomerInfoCoolDark.svg";
import { ReactComponent as Main } from "../../../assets/images/onboarding/main.svg";
import { ReactComponent as BuildingIcon } from "../../../assets/images/subscriptions/building-line.svg";

export default function OnboardingForm({
  history, statusMessage, setStatusMessage, addCompany = false,
}) {
  const { search } = useLocation();
  const [stepItems, setStepItems] = useState([{
    title: <CustomerInfoCoolDark />,
    description: "Customer Info",
    disabled: false,
    formName: "company_info_form",
    icon: <Main />,
  },
  {
    title: <GenistaLogo />,
    description: "Genista",
    disabled: true,
    formName: "fields_form",
    icon: <Main />,
  },
  {
    title: <BuildingIcon />,
    description: "Your Locations",
    disabled: true,
    formName: "locations_form",
    icon: <Main />,
  },
  {
    title: <AccountCreation />,
    description: "Account Creation",
    disabled: true,
    formName: "signup_form",
    icon: <Main />,
  },
  ]);
  const [stepChangeInProgress, setStepChangeInProgress] = useState(null); // { diff: Number } if step change in progress set diff (ex: -2, -1, 1, 2 etc.) else null
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [invalidMessage, setInvalidMessage] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    company_info_form: {
      // company_name: "",
      customer_name: "",
      customer_email: "",
      distribution_list: null,
      same_address: false,
    },
    company_contact_form: {
      same_as_address: false,
    },
    fields_form: {
      product_info: {
        sample_type: "Sample Type",
        date: "Received Date",
        physical_category: "Physical Category",
        editable_fields: [
          { json_field: "item", title_field: "Item" },
          { json_field: "lot", title_field: "Lot" },
          { json_field: "description", title_field: "Description" },
        ],
        sample_supply: "",
        product_link: [],
        add_company: "",
      },
      environment_info: {
        sample_type: "Sample Type",
        date: "Received Date",
        physical_category: "Physical Category",
        swab_number: "Swab Number",
        zone: "Zone",
        section: "Section",
        editable_fields: [],
        env_sample_supply: "",
        environment_link: [],
        environment_supplies_needed: "",
        env_add_company: "",
      },
    },
    link_report_form: {},
    locations_form: {
      locations: [],
    },
    signup_form: {},
  });
  const [currentFormIdx, setCurrentFormIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [existingUser, setExistingUser] = useState(false);
  const [userMail, setUserMail] = useState("");
  const [display, setDisplay] = useState(true);
  const [isCustomerExist, setIsCustomerExist] = useState(false);
  const [isSupport, setIsSupport] = useState(false);
  const [locationSidebar, setLocationSidebar] = useState({ locations: [], isAdding: false });
  const defaultPayloadValues = useRef({ sales_email: "", csm_email: "" });
  const submitInFlight = useRef(false);
  const [displaySuccessScreen, setDisplaySuccessScreen] = useState(false);
  const [userCredential, setUserCredentials] = useState("");
  const [primaryManagementType, setPrimaryManagementType] = useState(null);
  const [pendingManagementType, setPendingManagementType] = useState(null);
  const [showManagementChoice, setShowManagementChoice] = useState(false);
  const [disableExistingAccount, setDisableExistingAccount] = useState(false);
  const [customerType, setCustomerType] = useState(null);
  const normalizedCustomerType = normalizeCustomerType(customerType);
  const isNormalOrPostpay = isNormalOrPostpayCustomer(customerType);
  const isSubscriptionCustomer = normalizedCustomerType === "subscription";
  const isPrepaidCustomer = normalizedCustomerType === "prepaid";
  // Skip "Add New Location" choice: normal and postpay always, or prepaid Add Company when current company is independent
  const skipLocationManagementChoice = isNormalOrPostpay
    || (addCompany && isPrepaidCustomer && disableExistingAccount);
  const onStepChange = (value) => {
    if (value >= currentFormIdx) {
      return;
    }
    for (let i = value + 1; i < stepItems.length; i++) {
      setStepItems((prevStepItems) => prevStepItems.map((stepItem, stepIdx) => {
        if (i === stepIdx) {
          return { ...stepItem, title: <RenderIconOnPreviousClickForCustomerExist Inx={i} /> };
        }
        return stepItem;
      }));
    }
    setStepChangeInProgress({ diff: value - currentFormIdx });
  };

  /**
   * Save current form data and go to new step
   * @param {String} formName name of form
   * @param {Object} formValues values of form
   * @param {Number} _newIdx index of new step
   */
  const handleFormStepChange = (formName, formValues, diff, additionalData = {}) => {
    setFormData((prev) => {
      const next = { ...prev, [formName]: formValues, ...additionalData };
      // Customer Info edits location 1. Keep every other invitation location.
      if (
        formName === "company_info_form"
        && !additionalData.locations_form
        && prev.locations_form?.locations?.length
      ) {
        const editedAddress = formValues.address;
        const hasEditedAddress = editedAddress
          && Object.values(editedAddress).some((value) => String(value || "").trim());
        next.locations_form = {
          ...prev.locations_form,
          locations: prev.locations_form.locations.map((location, index) => {
            if (index !== 0) return location;
            return {
              ...location,
              name: formValues.company_name || location.name,
              company_name: formValues.company_name || location.company_name,
              customer_name: formValues.customer_name || location.customer_name,
              customer_email: formValues.customer_email || location.customer_email,
              distribution_list: formValues.distribution_list?.length
                ? formValues.distribution_list
                : location.distribution_list,
              address: hasEditedAddress ? editedAddress : location.address,
            };
          }),
        };
      }
      // Location 1 is submitted from the main forms, so card edits must be written back to them.
      const primaryLocation = formName === "locations_form" ? formValues.locations?.[0] : null;
      if (primaryLocation?.editedOnCard) {
        next.company_info_form = {
          ...prev.company_info_form,
          company_name: primaryLocation.company_name || prev.company_info_form?.company_name,
          customer_name: primaryLocation.customer_name || prev.company_info_form?.customer_name,
          customer_email: primaryLocation.customer_email || prev.company_info_form?.customer_email,
          distribution_list: primaryLocation.distribution_list?.length
            ? primaryLocation.distribution_list
            : prev.company_info_form?.distribution_list,
          address: primaryLocation.address || prev.company_info_form?.address,
        };
        next.fields_form = {
          ...prev.fields_form,
          product_info: primaryLocation.product_info || prev.fields_form?.product_info,
          environment_info: primaryLocation.environment_info || prev.fields_form?.environment_info,
        };
        if (primaryLocation.contact_info) {
          next.company_contact_form = {
            ...prev.company_contact_form,
            ...primaryLocation.contact_info,
          };
        }
      }
      return next;
    });
    if (stepChangeInProgress) {
      setStepChangeInProgress(null);
    }
    setCurrentFormIdx(currentFormIdx + diff);
  };

  const onBackButton = (formName, formValues) => {
    setStepItems(
      (prevStepItems) => prevStepItems.map((stepItem, stepIdx) => {
        if (currentFormIdx === stepIdx) {
          return { ...stepItem, title: <RenderIconOnPreviousClickForCustomerExist Inx={currentFormIdx} /> };
        }
        return stepItem;
      }),
    );
    handleFormStepChange(formName, formValues, -1); // save form, go to prev step
  };

  const onNextButton = (formName, formValues, additionalData = {}) => {
    handleFormStepChange(formName, formValues, 1, additionalData); // save form, go to next step
  };

  const handleSubmit = (formName, formValues) => {
    handleFormStepChange(formName, formValues, 0); // save form, stay on current step
    setSubmitting(true);
  };

  /* Update which steps are disabled when currentFormIdx changes */
  useEffect(() => {
    setStepItems((prevStepItems) => prevStepItems.map((stepItem, stepIdx) => {
      if (currentFormIdx === stepIdx) {
        return { ...stepItem, title: <RenderIconOnNextClickForCustomerExist Inx={currentFormIdx} /> };
      }
      return stepItem;
    }));
    setStepItems((prevStepItems) => prevStepItems.map((stepItem, stepIdx) => ({ ...stepItem, disabled: submitting || stepIdx > currentFormIdx })));
  }, [currentFormIdx, submitting, isCustomerExist]); // eslint-disable-line

  /* Show company name on the Locations step label when available */
  useEffect(() => {
    const locationName = formData.locations_form?.locations?.[0]?.name
      || formData.company_info_form?.company_name;
    if (!locationName) return;
    setStepItems((prevStepItems) => prevStepItems.map((stepItem) => (
      stepItem.formName === "locations_form"
        ? { ...stepItem, description: locationName }
        : stepItem
    )));
  }, [formData.company_info_form?.company_name, formData.locations_form?.locations]); // eslint-disable-line

  const handleLocationsSidebarChange = useCallback((payload) => {
    setLocationSidebar((prev) => ({ ...prev, ...payload }));
  }, []);

  const genistaFormIdx = stepItems.findIndex((step) => step.formName === "fields_form");
  const locationsFormIdx = stepItems.findIndex((step) => step.formName === "locations_form");
  const isOnLocationsStep = currentFormIdx === locationsFormIdx;
  const hasCrossedGenista = currentFormIdx > genistaFormIdx;
  const persistedLocations = formData.locations_form?.locations || [];

  const sidebarItems = useMemo(() => {
    const items = [];
    const sidebarLocations = locationSidebar.locations;
    const locationItems = isOnLocationsStep && sidebarLocations?.length
      ? sidebarLocations
      : persistedLocations;
    stepItems.forEach((step, formIdx) => {
      if (step.formName === "locations_form") {
        // Multiple invitation locations, or any subscription location, stay listed under Genista from the start.
        const showPrefilledLocations = locationItems.length > 1
          || (isSubscriptionCustomer && locationItems.length > 0);
        if ((!hasCrossedGenista && !showPrefilledLocations) || !locationItems.length) return;
        locationItems.forEach((location) => {
          items.push({
            ...step,
            description: location.name,
            formIdx,
            locationId: location.id,
            isLocationItem: true,
            isNewLocation: false,
          });
        });
        if (locationSidebar.isAdding) {
          items.push({
            ...step,
            description: "New Location",
            formIdx,
            isLocationItem: true,
            isNewLocation: true,
          });
        }
        return;
      }
      items.push({
        ...step,
        formIdx,
        isLocationItem: false,
        isNewLocation: false,
      });
    });
    return items;
  }, [stepItems, isOnLocationsStep, hasCrossedGenista, locationSidebar.locations, locationSidebar.isAdding, persistedLocations, isSubscriptionCustomer]);

  const sidebarCurrent = useMemo(() => {
    if (locationSidebar.isAdding && isOnLocationsStep) {
      const newLocationIdx = sidebarItems.findIndex((item) => item.isNewLocation);
      if (newLocationIdx !== -1) return newLocationIdx;
    }
    if (isOnLocationsStep && locationSidebar.editingLocationId) {
      const editingIdx = sidebarItems.findIndex(
        (item) => item.locationId === locationSidebar.editingLocationId,
      );
      if (editingIdx !== -1) return editingIdx;
    }
    const matchingIndexes = sidebarItems
      .map((item, index) => (item.formIdx === currentFormIdx ? index : -1))
      .filter((index) => index !== -1);
    if (!matchingIndexes.length) return currentFormIdx;
    return matchingIndexes[matchingIndexes.length - 1];
  }, [sidebarItems, currentFormIdx, locationSidebar.isAdding, locationSidebar.editingLocationId, isOnLocationsStep]);

  const onSidebarChange = (displayIndex) => {
    const item = sidebarItems[displayIndex];
    if (!item) return;
    onStepChange(item.formIdx);
  };

  const renderSidebarIcon = (item) => {
    if (item.isLocationItem) {
      return <BuildingIcon />;
    }
    if (item.formIdx < currentFormIdx) {
      return <CheckOutlined className="OnboardingForm__StepCheck" />;
    }
    return item.title;
  };

  /** Verify link and load form */
  useEffect(() => {
    async function handleParseLink() {
      try {
        const searchParams = new URLSearchParams(search);
        const uniqueInvitationId = searchParams.get("unique_invitation_id");

        /** Subscription / CPQ invite: /onboarding?unique_invitation_id=<id> */
        if (uniqueInvitationId) {
          const { success, message, result } = await getCustomerInvitation(uniqueInvitationId);
          if (!success || !result?.length) {
            setLoading(false);
            setInvalidMessage(message);
            return;
          }

          const primary = result[0] || {};
          const companyName = primary.company_name || "";
          const customerName = primary.customer_name || "";
          const customerEmail = primary.customer_email || "";
          const salesEmail = primary.sales_email || "";
          const csmEmail = primary.csm_email || "";
          const invitationCustomerType = normalizeCustomerType(primary.customer_type);
          const invitationCustomerId = primary.customer_id || searchParams.get("customer_id");

          if (!companyName || !customerEmail) {
            setLoading(false);
            setInvalidMessage("Invalid link");
            return;
          }

          const invitationLocations = buildInvitationLocations(result, {
            customerName,
            customerEmail,
          });

          const primaryAddress = invitationLocations[0]?.address || {};

          const normalizedInvitationLocations = isNormalOrPostpayCustomer(invitationCustomerType)
            ? invitationLocations.map((location) => ({
              ...location,
              managementType: "independent",
            }))
            : invitationLocations;

          const initialFormValues = {
            ...formData,
            company_info_form: {
              company_name: companyName,
              customer_name: customerName,
              customer_email: customerEmail,
              distribution_list: [customerEmail].filter(Boolean),
              ...(normalizedInvitationLocations.length
                ? { address: normalizedInvitationLocations[0]?.address || primaryAddress }
                : {}),
            },
            locations_form: {
              locations: normalizedInvitationLocations,
            },
          };

          setUserMail(customerEmail);
          setCustomerType(invitationCustomerType || null);
          if (isNormalOrPostpayCustomer(invitationCustomerType)) {
            setPrimaryManagementType("independent");
            setPendingManagementType("independent");
          }
          // Billing is always entered by the user; neither API returns billing details.
          if (invitationCustomerId) {
            setIsCustomerExist(true);
          }
          defaultPayloadValues.current = {
            sales_email: salesEmail,
            csm_email: csmEmail,
            unique_invitation_id: uniqueInvitationId,
            customer_type: invitationCustomerType,
            ...(invitationCustomerId ? {
              customer_id: invitationCustomerId,
              company_info: { distribution_list: [] },
            } : {}),
          };
          setFormData(initialFormValues);
          setLoaded(true);
          return;
        }

        if (addCompany) {
          const userCredentials = await getUserCredentials();
          setUserCredentials(userCredentials);
          setIsSupport(userCredentials?.uid === "support");
          const emails = userCredentials.user_email || "";
          const initialFormValues = {
            ...formData,
            company_info_form: {
              company_name: "",
              customer_name: userCredentials.user_name,
              customer_email: emails,
              distribution_list: [emails].filter(Boolean),
            },
          };
          setUserMail(emails);
          /* In case of the support  */
          const default_payload_values = {
            company_info: { distribution_list: [emails].filter(Boolean) },
            // customer_id: customerData.customer_id,
            company_name: userCredentials.company_name,
          };

          setStepItems(stepItems.filter(({ formName }) => formName !== "signup_form"));

          defaultPayloadValues.current = default_payload_values;
          setIsCustomerExist(true);
          setFormData(initialFormValues);

          const addCompanyCustomerType = normalizeCustomerType(
            getCustomerConfig()?.customer_type,
          );
          setCustomerType(addCompanyCustomerType);

          const { company_list } = await getCompanyList();
          const isIndependent = getCompanyIndependentFlag(
            company_list,
            userCredentials.company_name,
          );
          setDisableExistingAccount(isIndependent);
          if (
            isNormalOrPostpayCustomer(addCompanyCustomerType)
            || (addCompanyCustomerType === "prepaid" && isIndependent)
          ) {
            // Skip "Add New Location" choice; every location is independent
            setPrimaryManagementType("independent");
            setPendingManagementType("independent");
          } else if (isIndependent) {
            setPendingManagementType("independent");
          }
          setLoaded(true);
        } else {
          setLoading(false);
          setInvalidMessage("Invalid link");
        }
      } catch (e) {
        setLoading(false);
        setInvalidMessage("Invalid link");
      }
    }

    handleParseLink();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Temp Fix for UI issues on mount, setTimeout() fixes the issue */
  useEffect(() => {
    if (loaded) {
      setTimeout(() => {
        setLoading(false);
        setShowForm(true);
      }, 200);
    }
  }, [loaded]);

  /** Submit form */
  useEffect(() => {
    function trimValues(dataObj) {
      return mapValues(dataObj, (fieldVal) => { // trim all vals
        if (typeof fieldVal === "string") {
          return fieldVal.trim();
        }
        if (typeof fieldVal === "object" && fieldVal.constructor === Array) {
          return fieldVal.map((val) => val.trim()); // trim all vals in array
        }
        if (typeof fieldVal === "object") {
          return trimValues(fieldVal); // recursively trim
        }
        return "";
      });
    }

    async function handleSubmitForm() {
      const companyInfoForm = formData.company_info_form || {};
      const contactForm = formData.company_contact_form || {};
      const fieldsForm = formData.fields_form || {};
      const signupForm = formData.signup_form || {};
      const {
        sales_email,
        csm_email,
        customer_id,
        unique_invitation_id,
      } = defaultPayloadValues.current;

      const company_info = trimValues({
        customer_name: companyInfoForm.customer_name,
        company_name: companyInfoForm.company_name,
        distribution_list: companyInfoForm.distribution_list,
        customer_email: companyInfoForm.customer_email,
      });

      const address = companyInfoForm.address
        ? trimValues({
          city: companyInfoForm.address.city,
          country: companyInfoForm.address.country,
          line_2: companyInfoForm.address.line_2,
          state: companyInfoForm.address.state,
          street: companyInfoForm.address.street,
          zipcode: companyInfoForm.address.zipcode,
        })
        : {};

      const billing = contactForm.billing || {};
      const contact = trimValues({
        address,
        contact_name: contactForm.contact_name,
        position: contactForm.position,
        email: contactForm.email,
        phone_number: contactForm.phone_number,
        billing: {
          bill_to: billing.bill_to,
          ap_contact: billing.ap_contact,
          email: billing.email,
          phone_number: billing.phone_number,
          address: billing.address || {},
        },
      });
      if (contactForm.same_as_address) {
        contact.billing.address = contact.address;
      }

      const signup = trimValues({
        company_name: addCompany
          ? companyInfoForm.company_name
          : signupForm.company_name,
        email: signupForm.email,
        phone_number: signupForm.phone_number,
        customer_name: company_info.customer_name,
      });
      if (signupForm.password) {
        signup.password = signupForm.password;
      }
      if (addCompany) {
        signup.company_name = companyInfoForm.company_name;
        signup.email = company_info.customer_email;
      }

      /** Whitelist only the usersignuponboard contract keys (same shape for invite + add company) */
      const payload = {
        base_url: window.location.origin,
        sales_email: addCompany ? "" : (sales_email ?? ""),
        csm_email: addCompany ? "" : (csm_email ?? ""),
        current_company_name: "",
        existing_user: Boolean(addCompany || existingUser),
        locations: buildOnboardingLocations({
          locations: formData.locations_form?.locations ?? [],
          contact,
          companyInfo: company_info,
          productInfo: buildProductInfoPayload(fieldsForm.product_info),
          environmentInfo: buildEnvironmentInfoPayload(fieldsForm.environment_info),
          signup,
          customerId: (!addCompany && customer_id) ? customer_id : "",
          sameAsAddress: Boolean(contactForm.same_as_address),
          primaryManagementType: primaryManagementType
            || (skipLocationManagementChoice ? "independent" : null),
        }),
        users: buildOnboardingUsers(signup),
      };

      if (!addCompany && unique_invitation_id) {
        payload.unique_invitation_id = unique_invitation_id;
      }
      if (addCompany) {
        // Logged-in add-company flow only (not part of invite contract)
        payload.current_company_name = (await getUserCredentials())?.company_name ?? "";
        payload.disableInterceptor = true;
      }

      const { success, message } = await submitOnboardingForm(payload);
      setSubmitting(false);
      if (success) {
        const newMessage = message === "Success" ? "Signup successful" : `Warning: ${message}`;
        if (addCompany) {
          setDisplaySuccessScreen(true);
        } else if (statusMessage !== newMessage) {
          setStatusMessage(newMessage);
          setSubmitSuccess(true);
          toast.success(newMessage);
          history.push("/login");
        } else {
          history.push("/login");
        }
      } else if (addCompany) {
        toast.error(message);
      } else {
        setStatusMessage("Something went wrong!");
        setSubmitSuccess(false);
        toast.error(message);
      }
    }

    if (submitting && !submitInFlight.current) {
      submitInFlight.current = true;
      handleSubmitForm().finally(() => {
        submitInFlight.current = false;
      });
    }
  }, [formData, submitting]); // eslint-disable-line react-hooks/exhaustive-deps

  /** After status message is updated, go to success page */
  useEffect(() => {
    if (submitSuccess) {
      history.push("/login");
    }
  }, [statusMessage, submitSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Show toast error when validation fails on next/submit */
  const onFinishFailed = () => {
    toast.error("Invalid input");
  };

  const emailValidator = async (_, value) => {
    if (!value || validateEmail(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Invalid email"));
  };

  const phoneValidator = async (_, value) => {
    if (!value || validatePhoneNumber(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Invalid phone number"));
  };

  const handleWelcomeContinue = (nextDisplay) => {
    setDisplay(nextDisplay);
    if (nextDisplay === false) {
      if (skipLocationManagementChoice) {
        // Every company/location is independent; skip manage-under choice
        setPrimaryManagementType("independent");
        setFormData((prev) => {
          const existingLocations = prev.locations_form?.locations || [];
          if (!existingLocations.length) return prev;
          return {
            ...prev,
            locations_form: {
              ...prev.locations_form,
              locations: existingLocations.map((location) => ({
                ...location,
                managementType: "independent",
              })),
            },
          };
        });
        return;
      }
      // Prepaid / subscription invite: skip manage-under choice before location 1
      if (!addCompany && (isPrepaidCustomer || isSubscriptionCustomer)) {
        return;
      }
      setShowManagementChoice(true);
    }
  };

  const handleManagementChoiceBack = () => {
    setShowManagementChoice(false);
    setDisplay(true);
  };

  const handleManagementChoiceNext = async () => {
    if (!pendingManagementType) return;
    setPrimaryManagementType(pendingManagementType);

    // Add Company + Manage under existing: prefill from current company (location 1)
    if (addCompany && pendingManagementType === "existing") {
      try {
        const customerInfoResp = await getCustomerInfo();
        if (customerInfoResp?.success || customerInfoResp?.message === "Success") {
          const locationAddress = parseCustomerInfoLocationAddress(customerInfoResp);
          setFormData((prev) => ({
            ...prev,
            company_info_form: {
              ...prev.company_info_form,
              address: locationAddress,
              same_address: true,
            },
            locations_form: {
              ...prev.locations_form,
              locations: (prev.locations_form?.locations || []).map((location, index) => (
                index === 0
                  ? { ...location, managementType: pendingManagementType, address: locationAddress }
                  : location
              )),
            },
          }));
          setShowManagementChoice(false);
          return;
        }
      } catch (e) {
        // Fall through to default management-type update if prefill fails
      }
    }

    setFormData((prev) => {
      const existingLocations = prev.locations_form?.locations || [];
      if (!existingLocations.length) return prev;
      return {
        ...prev,
        locations_form: {
          ...prev.locations_form,
          locations: existingLocations.map((location, index) => (
            index === 0
              ? { ...location, managementType: pendingManagementType }
              : location
          )),
        },
      };
    });
    setShowManagementChoice(false);
  };

  const handleBackFromCompanyInfo = () => {
    if (
      skipLocationManagementChoice
      || (!addCompany && (isPrepaidCustomer || isSubscriptionCustomer))
    ) {
      setDisplay(true);
      return;
    }
    setShowManagementChoice(true);
  };

  if (loading) {
    return (
      <FormPageLayout addCompany={addCompany}>
        {/* <div className="OnboardingForm__Loading">
          <LoadingDots width={32} />
        </div> */}
      </FormPageLayout>
    );
  }

  if (!showForm) {
    return (
      <FormPageLayout addCompany={addCompany}>
        <div className="OnboardingForm__InvalidLink">
          <NoResults message={invalidMessage} image={pumpkinIcon} />
        </div>
      </FormPageLayout>
    );
  }

  if (displaySuccessScreen) {
    const companyName = formData.company_info_form.company_name?.trim();
    return (
      <AddCompanySuccessLandingPage newCompanyName={companyName} user_Title={userCredential?.user_title} currCompanyName={userCredential?.company_name} />
    );
  }
  return (
    <FormPageLayout addCompany={addCompany}>
      {display ? (
        <OnboardingWelcomePage setDisplay={handleWelcomeContinue} display={display} history={history} statusMessage={statusMessage} setStatusMessage={setStatusMessage} customerName={formData.company_info_form.customer_name} CustomerExist={isCustomerExist} addCompany={addCompany} />
      )
        : (
          <>
            <div className="OnboardingForm__StepsContainer">
              <Steps
                items={sidebarItems.map((item) => ({
                  title: item.description,
                  disabled: item.disabled,
                  className: item.isLocationItem ? "OnboardingForm__StepItem--location" : undefined,
                  icon: (
                    <span className={`OnboardingForm__StepIcon${item.isLocationItem ? " OnboardingForm__StepIcon--location" : ""}`}>
                      {renderSidebarIcon(item)}
                    </span>
                  ),
                }))}
                current={sidebarCurrent}
                className="OnboardingForm__Steps"
                direction="vertical"
                onChange={onSidebarChange}
              />
              <Button
                type="default"
                className="OnboardingForm__ExitBtn"
                onClick={() => history.push(addCompany ? "/sample-submission" : "/login")}
              >
                Exit
              </Button>
            </div>
            <FormLayout title={stepItems[currentFormIdx].title} className="OnboardingFormLayout">
              {showManagementChoice ? (
                <MyForm
                  requiredMark={false}
                  name="management_choice"
                  onFinish={handleManagementChoiceNext}
                  className="OnboardingForm LocationsForm"
                >
                  <AddLocationChoice
                    selected={pendingManagementType}
                    onSelect={setPendingManagementType}
                    disableExisting={disableExistingAccount}
                  />
                  <Form.Item>
                    <FormButtons
                      handleBack={handleManagementChoiceBack}
                      nextDisabled={!pendingManagementType}
                    />
                  </Form.Item>
                </MyForm>
              ) : (
                <>
                  {stepItems[currentFormIdx].formName === "company_info_form" && (
                  <CompanyInfoForm
                    initialValues={formData.company_info_form}
                    onNextButton={onNextButton}
                    emailValidator={emailValidator}
                    onFinishFailed={onFinishFailed}
                    stepChangeInProgress={stepChangeInProgress}
                    handleFormStepChange={handleFormStepChange}
                // Existing invite: show Email + lock identity. Dist list + address always collected
                // in CompanyInfoForm (same as invite-new / normal), including prepaid/subscription.
                    isExistingCustomer={Boolean(
                      isCustomerExist || defaultPayloadValues.current.customer_id,
                    )}
                    disableIdentityFields={Boolean(
                      isCustomerExist || defaultPayloadValues.current.customer_id,
                    )}
                    addCompany={addCompany || false}
                    isSupport={isSupport}
                    userCredential={userCredential}
                    onBackToWelcome={handleBackFromCompanyInfo}
                    managementType={primaryManagementType}
                  />
                  )}
                  {stepItems[currentFormIdx].formName === "company_contact_form" && (
                  <CompanyContactForm
                    initialValues={formData.company_contact_form}
                    addressData={formData.company_info_form}
              // email={formData.company_info_form?.customer_email ?? ""}
                    companyName={formData.company_info_form?.company_name ?? ""}
                    emailValidator={emailValidator}
                    phoneValidator={phoneValidator}
                    onNextButton={onNextButton}
                    onBackButton={onBackButton}
                    onFinishFailed={onFinishFailed}
                    stepChangeInProgress={stepChangeInProgress}
                    handleFormStepChange={handleFormStepChange}
                  />
                  )}
                  {stepItems[currentFormIdx].formName === "fields_form" && (
                  <FieldsForm
                    initialValues={formData.fields_form}
                    onNextButton={onNextButton}
                    onBackButton={onBackButton}
                    onFinishFailed={onFinishFailed}
                    stepChangeInProgress={stepChangeInProgress}
                    handleFormStepChange={handleFormStepChange}
                    existingUser={existingUser}
                    addCompany={addCompany || false}
                    companyName={defaultPayloadValues.current.company_name || ""}
                    submitting={submitting}
                    contactInitialValues={formData.company_contact_form}
                    addressData={formData.company_info_form?.address}
                    emailValidator={emailValidator}
                    phoneValidator={phoneValidator}
                    managementType={primaryManagementType}
                  />
                  )}
                  {stepItems[currentFormIdx].formName === "locations_form" && (
                  <LocationsForm
                    initialValues={formData.locations_form}
                    companyInfo={formData.company_info_form}
                    contactInfo={formData.company_contact_form}
                    fieldsInfo={formData.fields_form}
                    onNextButton={onNextButton}
                    onBackButton={onBackButton}
                    onFinishFailed={onFinishFailed}
                    stepChangeInProgress={stepChangeInProgress}
                    handleFormStepChange={handleFormStepChange}
                    handleSubmit={handleSubmit}
                    addCompany={addCompany || false}
                    submitting={submitting}
                    onLocationsSidebarChange={handleLocationsSidebarChange}
                    emailValidator={emailValidator}
                    phoneValidator={phoneValidator}
                    primaryManagementType={primaryManagementType}
                    canAddLocation={!isSubscriptionCustomer}
                    isSubscriptionCustomer={isSubscriptionCustomer}
                    skipManagementChoice={skipLocationManagementChoice}
                  />
                  )}
                  {stepItems[currentFormIdx].formName === "link_report_form" && (
                  <LinkReportForm
                    initialValues={formData.link_report_form}
                    fieldsData={formData?.fields_form}
                    onNextButton={onNextButton}
                    onBackButton={onBackButton}
                    onFinishFailed={onFinishFailed}
                    stepChangeInProgress={stepChangeInProgress}
                    handleFormStepChange={handleFormStepChange}
                    isExistingCustomer={defaultPayloadValues.current.customer_id}
                  />
                  )}
                  {stepItems[currentFormIdx].formName === "signup_form" && (
                  <SignupForm
                    initialValues={formData.signup_form}
                    email={userMail ?? ""}
                    company_name={formData.company_info_form?.company_name ?? ""}
                    customer_name={formData.company_info_form?.customer_name ?? ""}
                    existingUser={existingUser}
                    setExistingUser={setExistingUser}
                    handleSubmit={handleSubmit}
                    onBackButton={onBackButton}
                    emailValidator={emailValidator}
                    phoneValidator={phoneValidator}
                    onFinishFailed={onFinishFailed}
                    stepChangeInProgress={stepChangeInProgress}
                    handleFormStepChange={handleFormStepChange}
                    submitting={submitting}
                  />
                  )}
                </>
              )}
            </FormLayout>
          </>
        )}
    </FormPageLayout>
  );
}
